/**
 * PATH: packages/ui/src/components/email/EmailHtmlBody.tsx
 * FILE_ID: UI-EMAIL-HTMLBODY-001
 * PURPOSE: THE single canonical email-body renderer. Sanitises +
 *          cid-rewrites `body_html` via `sanitizeEmailHtml`, falls
 *          back to a plain-text `<pre>`, and lays both out in the
 *          same `.prose` container with the image/table/iframe
 *          clamps the Email Hub reader uses.
 * WHY:     The Email Hub reader (`packages/email-hub/.../EmailReader`)
 *          and the Kanban card drawer (`packages/ui/.../EmailThreadView`)
 *          were rendering the SAME `body_html` two different ways — the
 *          hub via `sanitizeEmailHtml` + prose (inline `cid:` images
 *          resolve, dark-mode typography, hardened external imgs), the
 *          drawer via a raw `<iframe srcDoc>` with no cid rewrite (so
 *          inline images rendered as broken boxes and the typography
 *          diverged). Operator (2026-05-31): "improve the drawer for
 *          emails significantly as they should work off the same email
 *          rendering function as the email hub". This component IS that
 *          one function — both surfaces now consume it.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────┐   ┌────────────────┐
 *   │ body_html    │ → │ sanitizeEmailHtml     │ → │ .prose render  │
 *   │ + attachments│   │ (cid→signed-url, XSS) │   │ (dark-aware)   │
 *   └──────────────┘   └──────────────────────┘   └────────────────┘
 *        else text → <pre> · else → caller `fallback`
 * DEPENDENCIES:
 *   - ../../security/sanitizeHtml: the cid-rewrite + DOMPurify pass.
 *   - ../../lib/utils (cn): class merge.
 * RELATED:
 *   - EMAILHUB-FEATURE-COMPONENTS-EMAILREADER-001 (hub consumer)
 *   - UI-KANBAN-EMAIL-VIEW-001 (drawer consumer)
 */
import { useCallback, useRef, type CSSProperties, type ReactNode, type Ref } from 'react'

import { cn } from '../../lib/utils'
import {
  EMAIL_BODY_SCOPE_ATTR,
  sanitizeEmailHtml,
  type InlineAttachmentRef,
} from '../../security/sanitizeHtml'
import { useFitToWidth } from './useFitToWidth'
import './email-html-body.css'

export interface EmailHtmlBodyProps {
  /** Raw HTML body (sanitised + cid-rewritten internally). When
   *  present it wins over `text`. */
  html?: string | null
  /** Plain-text fallback rendered in a pre-wrap `<pre>` when there's
   *  no HTML body (also the seeded list-cache `body_preview` case). */
  text?: string | null
  /** Attachment list used to resolve `<img src="cid:…">` references to
   *  signed inline-image URLs. Without it inline images render broken —
   *  this is the exact gap the drawer had vs the hub. */
  attachments?: ReadonlyArray<InlineAttachmentRef> | null
  /** Rendered when neither `html` nor `text` is in hand (e.g. the
   *  hub's body-loading skeleton). Defaults to `null`. */
  fallback?: ReactNode
  /** Extra classes merged onto the prose container (e.g. `mb-8`, or a
   *  card frame for the drawer). */
  className?: string
  /** Forwarded to the prose container — the Email Hub attaches its
   *  inline-image-recovery capture-phase root here, so the listener
   *  survives body re-renders. */
  bodyRef?: Ref<HTMLDivElement>
  /** Body-only zoom factor (1 = 100%). Applied via the CSS `zoom`
   *  property on the rendered content so the email REFLOWS within its
   *  column as it scales (unlike `transform: scale`, which overflows
   *  and needs manual translate math). The host (Email Hub reader)
   *  owns + persists this and also wires ⌘/Ctrl-scroll (trackpad
   *  pinch); the Kanban drawer can opt in later by passing the prop.
   *  Defaults to 1 (no zoom) so every other consumer is unaffected. */
  zoom?: number
  /** Auto-shrink the body to FIT its column when its content is wider than the
   *  column and can't reflow (fixed-width banner image, fixed-px table cells, a
   *  long unbreakable token). Operator (recurring): "this email did not fit the
   *  frame". Reflowable emails already fit via `[&_table]:max-w-full`; this is
   *  the safety net so the rest scale to fit instead of scrolling off-frame.
   *  Only ever scales DOWN, only when overflowing, and is disabled while the
   *  host applies a manual `zoom` (≠1) so explicit zoom-in still wins. Defaults
   *  to false → byte-identical to before for every consumer that doesn't opt
   *  in. */
  fitToWidth?: boolean
}

/**
 * Does this string actually contain HTML markup, or is it plain text that
 * merely happens to live in a `body_html` field?
 *
 * BUG-2026-06-15-EMAIL-PLAINTEXT-IN-HTML-ONE-LINE — operator: "the original
 * email has a new line but in Quotech it's all on one line". Root cause:
 * plain-text bodies are frequently stored in `body_html` (e.g. the sent-copy
 * path writes `body` into both fields), and HTML render collapses `\n` to
 * spaces (default `white-space: normal`). When the "html" has no real tags we
 * must render it as text (pre-wrap) so newlines survive. A real markup body
 * (Gmail multipart HTML, sender newsletters) still takes the sanitised path.
 *
 * The regex looks for an actual element/comment/entity start so a plain
 * sentence like "a < b" (note the space) is NOT misclassified as markup.
 */
function looksLikeHtmlMarkup(s: string): boolean {
  return /<([a-z!/][^>]*)>|&[a-z]+;|&#\d+;/i.test(s)
}

/**
 * Does this markup contain any tag that actually produces a line break /
 * block boundary? Real rich emails (Gmail multipart, Outlook MsoNormal
 * paragraphs, newsletters) always do — they use <br>, <p>, <div>, tables,
 * lists, headings, etc. for layout.
 *
 * BUG-2026-06-15-EMAIL-PLAINTEXT-IN-THIN-HTML-SHELL — operator: "no new
 * line?". Plain-text bodies are frequently wrapped in a thin
 * `<html><head>…</head><body>…</body></html>` shell (Exchange / some SMTP
 * relays) with the line breaks left as bare `\n`. That trips
 * `looksLikeHtmlMarkup` (the <html>/<body> tags ARE markup), so we used to
 * take the HTML path and the `\n` collapsed to nothing — "Hi VibhutiThis is
 * a test email.". When the markup has NO line-breaking tag but DOES carry
 * raw newlines, it's really plain text wearing an HTML shell and must be
 * rendered pre-wrap so the newlines survive.
 */
function hasLineBreakingTags(s: string): boolean {
  return /<\s*(br|p|div|li|tr|table|thead|tbody|ul|ol|h[1-6]|blockquote|hr|pre|article|section|header|footer|figure|address)\b/i.test(
    s,
  )
}

/** Bold, links, spans, and images are real formatting. A paper that uses
 *  them must not be flattened to plain text just because it also has newlines. */
function hasInlineFormatting(s: string): boolean {
  return /<\s*(a|b|strong|em|i|u|s|span|font|img|sup|sub|code|mark|small|big|strike)\b/i.test(s)
}

/** Turn newlines that sit inside a text run into <br>. Newlines that are
 *  only source formatting between tags stay put, so a normal HTML email
 *  does not gain a blank line between every element. */
function promoteInlineNewlines(html: string): string {
  if (!/[\r\n]/.test(html) || typeof DOMParser === 'undefined') return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const targets: Text[] = []
  let current = walker.nextNode()
  while (current) {
    const parent = (current as Text).parentElement?.tagName
    const value = current.nodeValue || ''
    if (parent !== 'SCRIPT' && parent !== 'STYLE' && /[\r\n]/.test(value) && /\S/.test(value)) {
      targets.push(current as Text)
    }
    current = walker.nextNode()
  }
  if (targets.length === 0) return html
  for (const textNode of targets) {
    const parts = (textNode.nodeValue || '').split(/\r\n|\r|\n/)
    const frag = doc.createDocumentFragment()
    parts.forEach((part, index) => {
      if (index > 0) frag.appendChild(doc.createElement('br'))
      if (part) frag.appendChild(doc.createTextNode(part))
    })
    textNode.parentNode?.replaceChild(frag, textNode)
  }
  return doc.body.innerHTML
}

/** Strip a thin HTML shell down to its visible text. Only ever called on
 *  markup we've already classified as line-break-free (no <br>/<p>/<div>/…),
 *  so a naive tag strip is safe — we just drop head/script/style blocks
 *  first so their contents (title, CSS, JS) don't leak into the text. */
function stripThinHtmlToText(s: string): string {
  return s
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, '')
}

/**
 * Canonical email-body render. Pure I→O: (html|text|attachments) →
 * sanitised, themed DOM. No data fetching, no side effects — the host
 * owns loading/error/thread state and passes the resolved fields in.
 */
export function EmailHtmlBody({
  html,
  text,
  attachments,
  fallback = null,
  className,
  bodyRef,
  zoom,
  fitToWidth = false,
}: EmailHtmlBodyProps) {
  // The column (clipping box) and the scaled body node — measured by the
  // fit-to-width hook. `containerRef` is composed with the caller's `bodyRef`
  // so the Email Hub's inline-image-recovery listener still attaches here.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLElement | null>(null)
  const setContainer = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node
      if (typeof bodyRef === 'function') bodyRef(node)
      else if (bodyRef) (bodyRef as { current: HTMLDivElement | null }).current = node
    },
    [bodyRef],
  )
  // A single callback ref shared by the HTML and plain-text branches (only one
  // mounts). Typed on `HTMLElement` so it's assignable to both a `div` and a
  // `pre` ref without an unsafe cast.
  const setContent = useCallback((node: HTMLElement | null) => {
    contentRef.current = node
  }, [])

  // Manual zoom (host ⌘/Ctrl-scroll) takes precedence: auto-fit only runs at
  // the default 1× so an explicit zoom-in is never fought.
  const manualZoom = typeof zoom === 'number' ? zoom : 1
  const autoFitEnabled = fitToWidth && manualZoom === 1
  const fitZoom = useFitToWidth({
    enabled: autoFitEnabled,
    containerRef,
    contentRef,
    floor: 0.5,
    signal: html ?? text ?? '',
  })
  const appliedZoom = autoFitEnabled ? fitZoom : manualZoom
  // Only emit the `zoom` style when actually zoomed, so the default
  // (1) render is byte-identical to before and existing structural
  // tests stay green. `zoom` reflows content; the parent's
  // `overflow-x-auto` is the safety net if a zoomed-in email exceeds
  // the column.
  const zoomStyle: CSSProperties | undefined =
    appliedZoom !== 1 ? { zoom: appliedZoom } : undefined
  // Decide which body to render. Real HTML markup → sanitised HTML path.
  // A `body_html` that is actually plain text → fall through to the
  // pre-wrap `<pre>` path (using that text) so its newlines are preserved.
  // A formatted body sometimes lands in `text` (Graph contentType "Text"
  // wrapping real markup). Treat that as HTML so the paper is not shown
  // as a plain <pre>.
  const textIsHtml =
    !html && !!text && looksLikeHtmlMarkup(text) && (hasLineBreakingTags(text) || hasInlineFormatting(text))
  const rawHtml = html || (textIsHtml ? text : '') || ''
  const htmlIsMarkup = !!rawHtml && looksLikeHtmlMarkup(rawHtml)
  // Plain text wearing a thin HTML shell: markup is present, but it has no
  // line-breaking tag AND carries real newlines we'd otherwise collapse.
  // Inline formatting (a title in <b>, a link, a span) keeps the HTML path.
  // Prefer the clean `text` (body_text) when we have it; only fall back to
  // stripping the shell when we don't (see hasLineBreakingTags docstring).
  const pseudoPlain =
    htmlIsMarkup &&
    !hasLineBreakingTags(rawHtml) &&
    !hasInlineFormatting(rawHtml) &&
    /[\r\n]/.test(rawHtml)
  const renderAsText = !htmlIsMarkup || pseudoPlain
  const plainText = renderAsText
    ? text || (pseudoPlain ? stripThinHtmlToText(rawHtml) : html) || ''
    : ''
  return (
    <div
      ref={setContainer}
      className={cn(
        // `email-html-body--ui-type` also opts this subtree out of the
        // app's border-box reset. See email-html-body.css. Newsletter
        // photo cells are width+padding+border; border-box lets the
        // photo cover the first letters of the title.
        'prose prose-sm dark:prose-invert max-w-none email-html-body--ui-type',
        'break-words',
        // FIT-TO-COLUMN (operator 2026-06-01 "the email is not fitting the
        // area"): a wide HTML email was rendering marooned in a narrow
        // centred column. Root cause was the old forced table ``w-auto``
        // override — it shrank EVERY table (including the full-width
        // wrapper table that's meant to fill the reader) to its text
        // content.
        // We now keep the email's intended widths and only CAP them to the
        // column (``[&_table]:max-w-full``), so a ``width="100%"`` wrapper
        // fills the pane and a ``width="600"`` block renders at 600 (capped
        // to the column) instead of collapsing.
        //
        // ``overflow-x-auto`` replaces the previous clip-or-collapse
        // behaviour as the safety net for the rare fixed-cell-width email
        // that still exceeds the column: it SCROLLS inside the body rather
        // than truncating the right edge (the original
        // BUG-2026-05-24-MOBILE-EMAIL-BODY-OVERFLOW symptom) or shrinking
        // to nothing.
        'overflow-x-auto',
        // IMAGE SIZING (BUG-2026-06-22-EMAIL-SIGNATURE-IMG-BLOWN-UP — operator:
        // "the image is fucked in the email on the signature"). `max-w-full`
        // caps every image to the column (so a wide screenshot can't overflow).
        // Height is the subtle part: a BLANKET `[&_img]:h-auto` emits
        // `.prose img { height: auto }`, and an author stylesheet rule BEATS the
        // presentational `height="N"` ATTRIBUTE that email signatures use to
        // size logos + social icons. A height-only icon (`<img height="24">`,
        // no width — the Apple-Mail / signature-generator idiom) then loses its
        // 24px and balloons to its intrinsic pixel size (the oversized stacked
        // Instagram/LinkedIn icons in the report). Inline `style="height:…"` is
        // safe (higher specificity than a class), but the bare attribute is not.
        // Fix: width-GATE the auto-height. `[&_img[width]]:h-auto` only forces
        // `height:auto` on images that declare a `width` (so width-capping still
        // scales their height proportionally — no distortion), and leaves
        // height-only / unsized images to the `height` attribute or the UA
        // default. Verified Tailwind v4 emits `& img[width] { height:auto }`.
        '[&_img]:max-w-full [&_img[width]]:h-auto',
        '[&_table]:max-w-full',
        '[&_iframe]:max-w-full',
        className,
      )}
    >
      {!renderAsText ? (
        // The scope attribute is what the sanitiser's <style>-rewrite
        // anchors to (BUG-2026-06-11-EMAIL-STYLE-LEAK): sender CSS can
        // only match elements inside THIS div, never the app shell.
        <div
          ref={setContent}
          style={zoomStyle}
          {...{ [EMAIL_BODY_SCOPE_ATTR]: '' }}
          dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(promoteInlineNewlines(rawHtml), attachments) }}
        />
      ) : plainText ? (
        <pre
          ref={setContent}
          style={zoomStyle}
          className="whitespace-pre-wrap font-sans text-foreground bg-transparent p-0"
        >
          {plainText}
        </pre>
      ) : (
        fallback
      )}
    </div>
  )
}
